import React, { useState, useEffect } from 'react';
import { DNDEditor } from '../../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { getTemplateCache } from '../services/templateCache';

/**
 * DetailResolver - Flexible detail rendering system for StackContainer
 * 
 * This component handles the priority system for detail view rendering:
 * 1. Custom DetailComponent (highest priority)
 * 2. Force default template (forceDefaultDetail=true)
 * 3. Item's specific template (item.vi)
 * 4. Item's embedded card data (item.card)
 * 5. Default template (defaultDetailTemplate)
 * 6. Fallback message
 * 
 * Props:
 * - item: The qwanta item to display
 * - DetailComponent: Custom React component for detail view
 * - defaultDetailTemplate: Template ID to use as default
 * - forceDefaultDetail: If true, always use default template
 * - useCompact: Whether to use compact rendering
 * - debug: Enable debug logging
 */

const DetailResolver = ({ 
  item,
  DetailComponent = null,
  defaultDetailTemplate = null,
  forceDefaultDetail = false,
  useCompact = false,
  debug = false
}) => {
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolvedMode, setResolvedMode] = useState('none');

  // Determine what to render based on priority system
  const resolveDetailMode = () => {
    if (debug) {
      console.log('[DetailResolver] Resolving detail mode for item:', {
        itemId: item?._id,
        hasDetailComponent: !!DetailComponent,
        forceDefaultDetail,
        itemVi: item?.vi,
        itemCard: !!item?.card,
        defaultDetailTemplate,
        item
      });
    }

    // Priority 1: Custom DetailComponent
    if (DetailComponent) {
      setResolvedMode('custom');
      return 'custom';
    }

    // Priority 2: Force default template
    if (forceDefaultDetail && defaultDetailTemplate) {
      setResolvedMode('force-default');
      return 'template';
    }

    // Priority 3: Item's specific template (vi)
    if (item?.vi && !forceDefaultDetail) {
      setResolvedMode('item-vi');
      return 'template';
    }

    // Priority 4: Item's embedded card data
    if (item?.card && !forceDefaultDetail) {
      setResolvedMode('item-card');
      return 'card';
    }

    // Priority 5: Default template
    if (defaultDetailTemplate) {
      setResolvedMode('default');
      return 'template';
    }

    // Priority 6: Fallback
    setResolvedMode('fallback');
    return 'fallback';
  };

  // Get the template ID based on resolved mode
  const getTemplateId = () => {
    switch (resolvedMode) {
      case 'force-default':
      case 'default':
        return defaultDetailTemplate;
      case 'item-vi':
        return item?.vi;
      default:
        return null;
    }
  };

  // Load template when needed
  useEffect(() => {
    const mode = resolveDetailMode();
    
    if (mode === 'template') {
      const templateId = getTemplateId();
      
      if (!templateId) {
        setError('No template ID available');
        return;
      }

      if (debug) {
        console.log(`[DetailResolver] Loading template ${templateId} for mode ${resolvedMode}`);
      }

      setLoading(true);
      setError(null);

      getTemplateCache().fetchTemplate(templateId)
        .then(template => {
          if (!template) {
            throw new Error(`Template ${templateId} not found`);
          }

          const processedTemplate = getTemplateCache().replaceTemplateVariables(template, item);
          setTemplateData(processedTemplate);
          setLoading(false);

          if (debug) {
            console.log(`[DetailResolver] Template ${templateId} loaded and processed`);
          }
        })
        .catch(err => {
          console.error(`[DetailResolver] Error loading template ${templateId}:`, err);
          setError(err.message);
          setLoading(false);
        });
    } else {
      setTemplateData(null);
      setLoading(false);
      setError(null);
    }
  }, [item, defaultDetailTemplate, forceDefaultDetail, resolvedMode, debug]);

  // Render loading state
  if (loading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        <div>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>🔄 Loading...</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Mode: {resolvedMode}
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        color: '#d32f2f',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        <div>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>❌ Template Error</div>
          <div style={{ fontSize: '14px', marginBottom: '8px' }}>{error}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Mode: {resolvedMode} | Template: {getTemplateId()}
          </div>
        </div>
      </div>
    );
  }

  // Debug info overlay (only in debug mode)
  const DebugOverlay = () => {
    if (!debug) return null;

    return (
      <div style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        color: 'white',
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '10px',
        zIndex: 1000,
        maxWidth: '200px'
      }}>
        <div>Mode: {resolvedMode}</div>
        {getTemplateId() && <div>Template: {getTemplateId()}</div>}
        <div>Item: {item?._id}</div>
      </div>
    );
  };

  // Render based on resolved mode
  const renderContent = () => {
    switch (resolvedMode) {
      case 'custom':
        return <DetailComponent item={item} />;

      case 'force-default':
      case 'item-vi':
      case 'default':
        return (
          <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            <QwantaProvider _id={item._id}>
              <DNDEditor
                cardData={templateData}
                enabled={false}
                readOnly={true}
                stackMode={false}
                compact={useCompact}
              />
            </QwantaProvider>
          </div>
        );

      case 'item-card':
        return (
          <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            <QwantaProvider _id={item._id}>
              <DNDEditor
                cardData={item.card}
                enabled={false}
                readOnly={true}
                stackMode={false}
                compact={useCompact}
              />
            </QwantaProvider>
          </div>
        );

      case 'fallback':
      default:
        return (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <div>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>No Detail View</div>
              <div style={{ fontSize: '14px', marginBottom: '16px' }}>
                Configure a detail template or component
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                Item ID: {item?._id}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <DebugOverlay />
      {renderContent()}
    </div>
  );
};

export default DetailResolver;